import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { adminApi, API } from "../../lib/api";
import { Calendar as CalendarIcon, Plus, Trash2, X, Edit3, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const pad2 = (n) => String(n).padStart(2, "0");
const monthBounds = (year, monthIdx) => {
  const last = new Date(year, monthIdx + 1, 0).getDate();
  return {
    from: `${year}-${pad2(monthIdx + 1)}-01`,
    to: `${year}-${pad2(monthIdx + 1)}-${pad2(last)}`,
  };
};

export default function CalendarPanel({ can, readOnly = false }) {
  const editable = !readOnly && can?.("manage_calendar");
  const today = new Date();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  // Admin filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | event | holiday
  const [showPast, setShowPast] = useState(false);

  // Parent month-by-month nav
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-indexed

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (readOnly) {
        const { from, to } = monthBounds(year, month);
        params.from_date = from;
        params.to_date = to;
      } else {
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
      }
      const { data } = await axios.get(`${API}/calendar/events`, { params });
      setEvents(data || []);
    } catch {
      toast.error("Failed to load events");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate, readOnly, year, month]);

  const onSave = async (payload) => {
    try {
      if (payload.id) {
        await adminApi.patch(`/admin/calendar/events/${payload.id}`, payload);
        toast.success("Event updated");
      } else {
        await adminApi.post("/admin/calendar/events", payload);
        toast.success("Event added");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try {
      await adminApi.delete(`/admin/calendar/events/${e.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const tIso = todayIso();
    return events
      .filter((e) => (readOnly ? true : (typeFilter === "all" ? true : e.type === typeFilter)))
      .filter((e) => (readOnly || showPast ? true : (e.date || "") >= tIso))
      .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [events, typeFilter, showPast, readOnly]);

  // Group by month for the admin list display (parent view is already single-month)
  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((e) => {
      const d = new Date(e.date);
      const key = isNaN(d.getTime()) ? "other" : `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
      if (!out[key]) out[key] = [];
      out[key].push(e);
    });
    return out;
  }, [filtered]);

  const monthKeys = Object.keys(grouped).sort();
  const clearFilters = () => { setFromDate(""); setToDate(""); setTypeFilter("all"); setShowPast(false); };

  const prevMonth = () => {
    if (month === 0) { setYear(year - 1); setMonth(11); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(year + 1); setMonth(0); } else setMonth(month + 1);
  };
  const goThisMonth = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="calendar-panel">
      <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#648DE5]/15 text-[#0F1B47] flex items-center justify-center">
            <CalendarIcon size={18} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Events & holidays</h2>
            <p className="text-sm text-[#4A5568] mt-0.5">
              {editable
                ? "Add and manage school events and holidays. Parents see them on their portal."
                : "Upcoming school events and holidays."}
            </p>
          </div>
        </div>
        {editable && (
          <button
            onClick={() => { setEditing({ date: todayIso(), title: "", type: "event", note: "" }); setShowForm(true); }}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]"
            data-testid="calendar-add-btn"
          >
            <Plus size={14} /> Add event
          </button>
        )}
      </div>

      {/* Filters — admin only */}
      {!readOnly && (
        <div className="px-6 md:px-7 pt-5">
          <div className="flex flex-wrap items-end gap-3 bg-[#F5F5F0] rounded-2xl p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.16em] uppercase text-[#648DE5]">
              <Filter size={12} /> Filter
            </div>
            <FilterField label="From">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={filterInputCls} data-testid="calendar-filter-from" />
            </FilterField>
            <FilterField label="To">
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={filterInputCls} data-testid="calendar-filter-to" />
            </FilterField>
            <FilterField label="Type">
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={filterInputCls} data-testid="calendar-filter-type">
                <option value="all">All</option>
                <option value="event">Events</option>
                <option value="holiday">Holidays</option>
              </select>
            </FilterField>
            <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#0F1B47] cursor-pointer pb-1">
              <input type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} className="w-3.5 h-3.5 accent-[#0F1B47]" data-testid="calendar-filter-show-past" />
              Show past
            </label>
            {(fromDate || toDate || typeFilter !== "all" || showPast) && (
              <button onClick={clearFilters} className="text-[11px] font-semibold text-[#0F1B47] underline underline-offset-2 hover:opacity-70 pb-1" data-testid="calendar-filter-clear">
                Clear
              </button>
            )}
            <div className="ml-auto text-[11px] text-[#4A5568] pb-1">
              <span className="font-bold text-[#0F1B47]">{filtered.length}</span> {filtered.length === 1 ? "event" : "events"}
            </div>
          </div>
        </div>
      )}

      {/* Month navigator — parent only */}
      {readOnly && (
        <div className="px-6 md:px-7 pt-5">
          <div className="flex items-center justify-between bg-[#F5F5F0] rounded-2xl p-3" data-testid="calendar-month-nav">
            <button onClick={prevMonth} className="p-2 rounded-full hover:bg-[#0F1B47]/10 text-[#0F1B47]" title="Previous month" data-testid="calendar-prev">
              <ChevronLeft size={16} />
            </button>
            <div className="flex items-center gap-3">
              <div className="font-display text-lg text-[#0F1B47] font-medium" data-testid="calendar-month-label">
                {MONTHS[month]} {year}
              </div>
              <button onClick={goThisMonth} className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] px-3 py-1 rounded-full bg-[#FDFDF9] hover:bg-[#0F1B47]/5" data-testid="calendar-today">
                This month
              </button>
            </div>
            <button onClick={nextMonth} className="p-2 rounded-full hover:bg-[#0F1B47]/10 text-[#0F1B47]" title="Next month" data-testid="calendar-next">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* List / Parent split view */}
      <div className="p-6 md:p-7">
        {readOnly ? (
          loading ? (
            <div className="text-sm text-[#718096] py-10 text-center">Loading events…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5" data-testid="calendar-split">
              <div className="md:col-span-5">
                <MiniMonth
                  year={year}
                  month={month}
                  events={filtered}
                />
                <div className="mt-3 flex items-center gap-4 px-1 text-[10px] font-semibold text-[#4A5568]">
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#C6D947]" /> Event</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Holiday</span>
                </div>
              </div>
              <div className="md:col-span-7">
                {filtered.length === 0 ? (
                  <div className="bg-[#F5F5F0] rounded-2xl p-8 text-center h-full flex flex-col items-center justify-center">
                    <CalendarIcon size={24} className="text-[#648DE5] mb-2" />
                    <div className="text-[#0F1B47] font-medium text-sm">No events for {MONTHS[month]} {year}</div>
                    <p className="text-xs text-[#718096] mt-1">Check the next month or check back soon.</p>
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="calendar-list">
                    {filtered.map((e) => <EventRow key={e.id} event={e} editable={false} />)}
                  </ul>
                )}
              </div>
            </div>
          )
        ) : loading ? (
          <div className="text-sm text-[#718096] py-10 text-center">Loading events…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#F5F5F0] rounded-2xl p-10 text-center">
            <CalendarIcon size={28} className="mx-auto text-[#648DE5] mb-2" />
            <div className="text-[#0F1B47] font-medium">No events to show</div>
            <p className="text-xs text-[#718096] mt-1">
              {editable ? 'Click "Add event" to create your first event.' : "Check back soon — new events will appear here."}
            </p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="calendar-list">
            {monthKeys.map((mk) => {
              const [y, m] = mk.split("-").map(Number);
              const label = isNaN(y) ? "Other" : `${MONTHS[m - 1]} ${y}`;
              return (
                <div key={mk}>
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5] mb-2">{label}</div>
                  <ul className="space-y-2">
                    {grouped[mk].map((e) => (
                      <EventRow
                        key={e.id}
                        event={e}
                        editable={editable}
                        onEdit={() => { setEditing(e); setShowForm(true); }}
                        onDelete={() => onDelete(e)}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <EventForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function EventRow({ event: e, editable, onEdit, onDelete }) {
  return (
    <li
      className="bg-[#F5F5F0] rounded-2xl px-4 py-3 flex items-center gap-3 hover:bg-[#0F1B47]/5 transition-colors"
      data-testid={`calendar-row-${e.id}`}
    >
      <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center shrink-0 ${e.type === "holiday" ? "bg-red-100 text-red-700" : "bg-[#C6D947]/40 text-[#0F1B47]"}`}>
        <div className="text-[9px] font-bold tracking-[0.1em] uppercase leading-none">{MONTHS[new Date(e.date).getMonth()]?.slice(0, 3) || "—"}</div>
        <div className="text-[15px] font-bold leading-none mt-0.5">{new Date(e.date).getDate() || "—"}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[14px] font-semibold text-[#0F1B47] truncate">{e.title}</div>
          <span className={`text-[9px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-0.5 ${e.type === "holiday" ? "bg-red-100 text-red-700" : "bg-[#C6D947]/40 text-[#0F1B47]"}`}>
            {e.type}
          </span>
        </div>
        <div className="text-[11px] text-[#4A5568] mt-0.5">
          {formatDate(e.date)}{e.note ? ` · ${e.note}` : ""}
        </div>
      </div>
      {editable && (
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit} className="p-2 rounded-full text-[#0F1B47] hover:bg-[#0F1B47]/10" title="Edit" data-testid={`calendar-edit-${e.id}`}>
            <Edit3 size={13} />
          </button>
          <button onClick={onDelete} className="p-2 rounded-full text-red-600 hover:bg-red-50" title="Delete" data-testid={`calendar-delete-${e.id}`}>
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </li>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#4A5568] mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function EventForm({ initial, onClose, onSave }) {
  const [date_, setDate] = useState(initial?.date || todayIso());
  const [title, setTitle] = useState(initial?.title || "");
  const [type, setType] = useState(initial?.type || "event");
  const [note, setNote] = useState(initial?.note || "");
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!date_) return toast.error("Pick a date");
    if (!title.trim()) return toast.error("Add a title");
    setSaving(true);
    await onSave({ id: initial?.id, date: date_, title: title.trim(), type, note: note.trim() });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-3xl w-full max-w-md shadow-2xl" data-testid="calendar-event-form">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">{isEdit ? "Edit" : "Add"}</div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">{isEdit ? "Update event" : "New event"}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">Date *</span>
            <input type="date" value={date_} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="calendar-form-date" />
          </div>
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">Title *</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="e.g. Sports Day, Onam Holiday" className={inputCls} data-testid="calendar-form-title" />
          </div>
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">Type *</span>
            <div className="grid grid-cols-2 gap-2">
              {["event", "holiday"].map((t) => (
                <button
                  key={t} type="button" onClick={() => setType(t)}
                  className={`px-4 py-2 rounded-2xl text-sm font-semibold capitalize ${type === t ? (t === "holiday" ? "bg-red-100 text-red-700 border-2 border-red-300" : "bg-[#C6D947]/40 text-[#0F1B47] border-2 border-[#C6D947]") : "bg-[#F5F5F0] text-[#4A5568] border-2 border-transparent"}`}
                  data-testid={`calendar-form-type-${t}`}
                >{t}</button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">Note (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={400} placeholder="Any extra details parents should know" className={`${inputCls} resize-none`} data-testid="calendar-form-note" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3 bg-[#FDFDF9]">
          <button type="submit" disabled={saving} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="calendar-form-save">
            {saving ? "Saving…" : (isEdit ? "Update event" : "Add event")}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] placeholder:text-[#718096] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]";
const filterInputCls = "bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-xl px-3 py-1.5 text-[12px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5]";

const WEEKDAYS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

function MiniMonth({ year, month, events }) {
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leading = firstOfMonth.getDay();
  const today = new Date();
  const todayIsToday = (d) =>
    today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  // Build map: date number -> array of types in that day
  const byDay = {};
  events.forEach((e) => {
    const d = new Date(e.date);
    if (d.getFullYear() !== year || d.getMonth() !== month) return;
    const day = d.getDate();
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(e.type);
  });

  const cells = [];
  for (let i = 0; i < leading; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  return (
    <div className="bg-[#F5F5F0] rounded-2xl p-3 md:p-4" data-testid="calendar-mini-month">
      <div className="grid grid-cols-7 mb-1.5">
        {WEEKDAYS_SHORT.map((d, i) => (
          <div key={i} className="text-[9px] font-bold tracking-[0.1em] uppercase text-[#4A5568] text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`b${i}`} className="h-9" />;
          const types = byDay[d] || [];
          const hasHoliday = types.includes("holiday");
          const hasEvent = types.includes("event");
          const isToday = todayIsToday(d);
          return (
            <div
              key={d}
              className={`relative h-9 rounded-lg flex flex-col items-center justify-center text-[12px] font-semibold transition-colors ${
                hasHoliday
                  ? "bg-red-100 text-red-700"
                  : hasEvent
                    ? "bg-[#C6D947]/40 text-[#0F1B47]"
                    : "bg-[#FDFDF9] text-[#0F1B47]"
              } ${isToday ? "ring-2 ring-[#0F1B47] ring-offset-1 ring-offset-[#F5F5F0]" : ""}`}
              data-testid={`calendar-mini-day-${d}`}
            >
              <span>{d}</span>
              {(hasEvent || hasHoliday) && (
                <span className="absolute bottom-1 flex gap-0.5">
                  {hasHoliday && <span className="w-1 h-1 rounded-full bg-red-500" />}
                  {hasEvent && !hasHoliday && <span className="w-1 h-1 rounded-full bg-[#0F1B47]" />}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
